import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { PublishedLegend } from '@/features/jobs/legends/PublishedLegend'
import { action, computed, observable, runInAction } from 'mobx'
import {
  allLegendServiceTypes,
  type LegendServiceType,
} from '@/features/jobs/legends/legend-service-type'
import { uniq } from 'lodash-es'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'
import type { ListPublishedLegendsFn } from '@/features/jobs'
import type { GetCurrentMemberFn } from '@/features/iam'
import { type Observable, Subject, switchMap } from 'rxjs'
import { withAbortSignal } from '@/utils/rx-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { tap } from 'rxjs/operators'
import { tapAction } from '@/utils/mobx-util'
import { deepEqual } from '@/utils/object-util'
import type { CreateNewDraftJobFn } from '@/features/jobs/internal'
import { type SessionOptions } from '@/features/iam/auth/SessionOptions'

/**
 * Manages state relevant for creating a new job via service selection.
 */
export class ServiceSelectionViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _draftCreatedSubject = new Subject<Id>()

  /**
   * Emits when a new draft has been created.
   */
  readonly draftCreated$ = this._draftCreatedSubject.asObservable()

  /**
   * Modal for service types.
   */
  readonly serviceTypesModalVm = new ModalViewModel()

  /**
   * Modal for selecting a Legend.
   */
  readonly legendSelectionModalVm = new ModalViewModel()

  /**
   * Modal for service type info.
   */
  readonly serviceTypeInfoModalVm = new ModalViewModel()

  /**
   * Legends available for selection.
   */
  @observable accessor availableLegends: PublishedLegend[] = []

  /**
   * Selected service type, if any.
   */
  @observable accessor selectedServiceType: LegendServiceType | null = null

  /**
   * Selected service type for info dialog.
   */
  @observable accessor selectedServiceTypeForInfo: LegendServiceType | null =
    null

  /**
   * Selected Legend, if any.
   */
  @observable accessor selectedLegend: PublishedLegend | null = null

  constructor(
    private readonly _isUsingServiceTypesModal: () => boolean,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _sessionOptions$: Observable<SessionOptions>,
    private readonly _listPublishedLegends: ListPublishedLegendsFn,
    private readonly _createNewDraftJob: CreateNewDraftJobFn,
  ) {
    this._lifecycle.manage(
      this._sessionOptions$.pipe(
        switchMap((options) =>
          withAbortSignal((signal) =>
            this.listPublishedLegends(options?.allowedLegendIds ?? [], signal),
          ),
        ),
      ),
    )

    // Clear selections when corresponding modals are closed.
    this._lifecycle.manage(
      this.serviceTypesModalVm.close$.pipe(
        tap(() => this.selectServiceType(null)),
      ),
    )
    this._lifecycle.manage(
      this.legendSelectionModalVm.close$.pipe(
        tapAction(() => {
          this.selectLegend(null)
        }),
      ),
    )
    this._lifecycle.manage(
      this.serviceTypeInfoModalVm.close$.pipe(
        tapAction(() => {
          this.selectServiceTypeForInfo(null)
        }),
      ),
    )
  }

  /**
   * The current member's workspace.
   */
  @computed
  get workspace() {
    return this._getCurrentMember().workspace
  }

  /**
   * The service types.
   */
  @computed
  get availableServiceTypes() {
    const types = uniq(this.availableLegends.map((l) => l.serviceType))

    // Sorts the service types to the same order as the allLegendServiceTypes.
    return allLegendServiceTypes.filter((type) => types.includes(type))
  }

  /**
   * Legends by service type.
   */
  @computed
  get filteredLegends() {
    return this.availableLegends.filter(
      (l) => l.serviceType === this.selectedServiceType,
    )
  }

  /**
   * Whether any of the creation actions are pending.
   */
  @computed
  get isPending() {
    return this.createDraft.pending
  }

  /**
   * Maps to a service selection state
   */
  @computed
  get serviceSelectionState(): ServiceSelectionState {
    return {
      isShowingServiceSelection:
        this.serviceTypesModalVm.showing || this.legendSelectionModalVm.showing,
      selectedServiceType: this.legendSelectionModalVm.showing
        ? this.selectedServiceType
        : null,
    }
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the service types modal if there are multiple services to
   * choose from.
   *
   * @param isRestoring Whether we're restoring state, in which case we
   * don't want to trigger any side effects.'
   */
  @action.bound startNewService(isRestoring = false) {
    this._closeModals()

    if (this.availableLegends.length === 0) {
      // Can't do anything until we know what Legends we have.
      return
    }

    if (this.availableServiceTypes.length === 1) {
      // Service type selection is not available since there's
      // only 1 service available, so select that service
      // immediately.
      this.selectServiceType(this.availableServiceTypes[0], isRestoring)

      return
    }

    // Show the service types dialog if there's at least one job.
    if (this._isUsingServiceTypesModal()) {
      this.serviceTypesModalVm.show()
    }
  }

  /**
   * Set the service type.
   *
   * @param type
   * @param isRestoring Whether we're restoring state, in which case we
   * don't want to trigger any side effects.
   */
  @action.bound selectServiceType(
    type: LegendServiceType | null,
    isRestoring = false,
  ) {
    this.selectedServiceType = type
    if (type === null) {
      this.selectLegend(null)
      return
    }

    // Shows the Legends dialog.
    this._showLegendSelectionOrCreateDraft(isRestoring)
  }

  /**
   * Sets the selected Legend.
   *
   * @param legend
   */
  @action.bound selectLegend(legend: PublishedLegend | null) {
    this.selectedLegend = legend
  }

  /**
   * Continues the flow based on the current state.
   */
  @action.bound next() {
    if (!this.selectedServiceType) {
      return
    }

    this._showLegendSelectionOrCreateDraft(false)
  }

  /**
   * Select the service type for info display.
   */
  @action.bound selectServiceTypeForInfo(type: LegendServiceType | null) {
    this.selectedServiceTypeForInfo = type
  }

  /**
   * Shows the service type info dialog for the given service type.
   */
  @action.bound showServiceTypeInfo(type: LegendServiceType) {
    this.selectServiceTypeForInfo(type)
    this.serviceTypeInfoModalVm.show()
  }

  /**
   * Lists the published legends.
   */
  listPublishedLegends = task(
    async (allowedLegendIds: Id[], signal: AbortSignal) => {
      const publishedLegends = await this._listPublishedLegends(
        this._getCurrentMember().workspaceId,
        signal,
      )

      runInAction(() => {
        this.availableLegends =
          allowedLegendIds.length === 0
            ? publishedLegends
            : publishedLegends.filter((legend) =>
                allowedLegendIds.includes(legend.id),
              )
      })
    },
  )

  /**
   * Creates a new draft from a Legend.
   */
  createDraft = task.resolved(async (legend: PublishedLegend) => {
    const draft = await this._createNewDraftJob({
      legendVersionId: legend.versionId,
    })
    this._closeModals()
    this._draftCreatedSubject.next(draft.id)
  })

  /**
   * Shows Legend if applicable, or starts creating the job with
   * in the case of only a single Legend being available, or if
   * a Legend is already selected.
   *
   * @param isRestoring Whether we're restoring state, in which case we
   * don't want to trigger any side effects.
   */
  @action.bound private _showLegendSelectionOrCreateDraft(
    isRestoring: boolean,
  ) {
    if (this.filteredLegends.length === 1) {
      if (!isRestoring) {
        void this.createDraft(this.filteredLegends[0])
      }

      return
    }

    if (this.selectedLegend) {
      if (!isRestoring) {
        void this.createDraft(this.selectedLegend)
      }

      return
    }

    this.legendSelectionModalVm.show()
  }

  /**
   * Restores the selection state based on the given {@link state}.
   */
  @action.bound
  fromServiceSelectionState(state: ServiceSelectionState) {
    const current = this.serviceSelectionState
    if (deepEqual(current, state)) {
      return
    }

    if (!state.isShowingServiceSelection) {
      this._closeModals()
      return
    }

    if (state.isShowingServiceSelection) {
      this.startNewService(true)
    }

    if (current.selectedServiceType !== state.selectedServiceType) {
      this.selectServiceType(state.selectedServiceType, true)
    }
  }

  /**
   * Close all the models.
   *
   * @private
   */
  @action.bound
  private _closeModals() {
    this.serviceTypesModalVm.close()
    this.legendSelectionModalVm.close()
    this.serviceTypeInfoModalVm.close()
  }
}

/**
 * Service selection state from which the selection view model
 * can be restored.
 */
export interface ServiceSelectionState {
  /**
   * Whether any service selection modals are being displayed.
   */
  isShowingServiceSelection: boolean
  /**
   * The selected service type, if any.
   */
  selectedServiceType: LegendServiceType | null
}
